import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Trash2, Pencil, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { PageHeader, PrimaryButton, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ChallengeDialog from "@/components/ChallengeDialog";
import ConversationLogDialog from "@/components/ConversationLogDialog";
import { formatDeDate } from "@/lib/dateFormat";

const STATUS_META = {
  aktiv: { label: "Diese Woche aktiv", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  wartet: { label: "Wartet auf andere", color: "bg-amber-50 text-amber-700 border-amber-200" },
  ruhend: { label: "Bewusst ruhen", color: "bg-gray-100 text-gray-600 border-gray-200" },
  abgeschlossen: { label: "Abgeschlossen", color: "bg-blue-50 text-blue-700 border-blue-200" },
};

const PRIO_META = {
  hoch: { label: "Hoch", color: "text-rose-700" },
  mittel: { label: "Mittel", color: "text-amber-700" },
  niedrig: { label: "Niedrig", color: "text-gray-500" },
};

function fmt(d) {
  return formatDeDate(d);
}

export default function Challenges() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [convDialog, setConvDialog] = useState(null); // { contactId, contactName, challengeId }

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["challenges", q, statusFilter],
    queryFn: async () => {
      const params = {};
      if (q) params.q = q;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      return (await api.get(`/challenges`, { params })).data;
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/challenges/${id}`)).data,
    onSuccess: () => {
      toast.success("Herausforderung gelöscht");
      qc.invalidateQueries({ queryKey: ["challenges"] });
      setConfirmDelete(null);
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const grouped = useMemo(() => {
    const g = { aktiv: [], wartet: [], ruhend: [], abgeschlossen: [] };
    items.forEach((c) => {
      const key = STATUS_META[c.status] ? c.status : "aktiv";
      g[key].push(c);
    });
    return g;
  }, [items]);

  return (
    <div data-testid="challenges-page">
      <PageHeader
        title="Herausforderungen"
        subtitle="Themen, an denen du gerade dran bist – rund um Personen und Beziehungen."
        testid="challenges-header"
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Suchen…"
                className="pl-8 w-56"
                data-testid="challenges-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-52" data-testid="challenges-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="aktiv">Diese Woche aktiv</SelectItem>
                <SelectItem value="wartet">Wartet auf andere</SelectItem>
                <SelectItem value="ruhend">Bewusst ruhen</SelectItem>
                <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
              </SelectContent>
            </Select>
            <PrimaryButton
              onClick={() => {
                setEditing(null);
                setOpenDialog(true);
              }}
              data-testid="challenges-new-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Herausforderung erfassen
            </PrimaryButton>
          </div>
        }
      />

      {isLoading ? (
        <div className="crm-card p-8 text-center text-sm text-gray-500">Lädt…</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Noch keine Herausforderungen"
          description="Lege deine erste Herausforderung an, um Themen strukturiert zu verfolgen."
          action={
            <PrimaryButton onClick={() => setOpenDialog(true)} data-testid="challenges-empty-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Herausforderung erfassen
            </PrimaryButton>
          }
        />
      ) : (
        <div className="space-y-6">
          {["aktiv", "wartet", "ruhend", "abgeschlossen"].map((key) => {
            const list = grouped[key];
            if (!list || list.length === 0) return null;
            const meta = STATUS_META[key];
            return (
              <section key={key} data-testid={`challenges-group-${key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${meta.color}`}>
                    {meta.label}
                  </span>
                  <span className="text-xs text-gray-500">{list.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {list.map((ch) => (
                    <ChallengeRow
                      key={ch.id}
                      challenge={ch}
                      onEdit={() => {
                        setEditing(ch);
                        setOpenDialog(true);
                      }}
                      onDelete={() => setConfirmDelete(ch)}
                      onLogConversation={(contactId, contactName) =>
                        setConvDialog({ contactId, contactName, challengeId: ch.id, challenges: [ch] })
                      }
                      onContactClick={(id) => navigate(`/contacts/${id}`)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ChallengeDialog
        open={openDialog}
        onOpenChange={(v) => {
          setOpenDialog(v);
          if (!v) setEditing(null);
        }}
        editing={editing}
      />

      {convDialog ? (
        <ConversationLogDialog
          open={!!convDialog}
          onOpenChange={(v) => {
            if (!v) setConvDialog(null);
          }}
          contactId={convDialog.contactId}
          contactName={convDialog.contactName}
          challenges={convDialog.challenges}
        />
      ) : null}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="challenges-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Herausforderung wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>Dieser Vorgang kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="challenges-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => deleteMut.mutate(confirmDelete.id)}
              data-testid="challenges-delete-confirm"
            >
              Herausforderung löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ChallengeRow({ challenge, onEdit, onDelete, onLogConversation, onContactClick }) {
  const prio = PRIO_META[challenge.prioritaet] || PRIO_META.mittel;
  const needsDate = (challenge.status === "aktiv" || challenge.status === "ruhend") && !challenge.naechster_termin;
  return (
    <div className="crm-card p-4" data-testid={`challenges-row-${challenge.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-gray-900">{challenge.titel}</div>
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${prio.color}`}>
              Prio: {prio.label}
            </span>
          </div>
          {challenge.ziel ? <p className="text-sm text-gray-600 mt-1 line-clamp-2">{challenge.ziel}</p> : null}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
            onClick={onEdit}
            aria-label="Bearbeiten"
            data-testid={`challenges-edit-${challenge.id}`}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            className="p-1.5 rounded-md hover:bg-gray-100 text-rose-500"
            onClick={onDelete}
            aria-label="Löschen"
            data-testid={`challenges-delete-${challenge.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3 text-xs">
        <div>
          <div className="uppercase tracking-wider text-gray-500 mb-0.5">Letzter Schritt</div>
          <div className="text-gray-700">{challenge.letzter_schritt || <span className="text-gray-400 italic">—</span>}</div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-gray-500 mb-0.5">Nächster Schritt</div>
          <div className="text-gray-700">{challenge.naechster_schritt || <span className="text-gray-400 italic">—</span>}</div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-gray-500 mb-0.5">Nächster Termin</div>
          <div className={needsDate ? "text-amber-700" : "text-gray-700"}>
            {challenge.naechster_termin ? fmt(challenge.naechster_termin) : <span className="italic">—</span>}
          </div>
        </div>
      </div>

      {needsDate ? (
        <div className="text-[11px] text-amber-700 mt-2" data-testid={`challenges-missing-date-${challenge.id}`}>
          Freundlicher Hinweis: aktive/ruhende Herausforderungen sollten einen Termin haben.
        </div>
      ) : null}

      {challenge.contacts && challenge.contacts.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Beteiligte</div>
          <div className="flex flex-wrap gap-1">
            {challenge.contacts.map((link) => {
              const dot =
                link.ampel_status === "rot" ? "bg-rose-500"
                : link.ampel_status === "gelb" ? "bg-amber-400"
                : "bg-emerald-500";
              return (
                <button
                  key={link.id}
                  onClick={() => onContactClick?.(link.contact_id)}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-gray-50 hover:bg-gray-100 border border-gray-200"
                  data-testid={`challenges-contact-${link.contact_id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                  {link.contact_name}
                </button>
              );
            })}
          </div>
          {challenge.contacts.length > 0 ? (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() =>
                  onLogConversation?.(challenge.contacts[0].contact_id, challenge.contacts[0].contact_name)
                }
                data-testid={`challenges-log-conv-${challenge.id}`}
              >
                <MessageSquareText className="w-3 h-3 mr-1" /> Gespräch erfassen
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
